import { Box } from "@components/Layout"
import SEO from "@components/SEO"
import { breakpoints } from "@config/theme"
import { ComponentFilter } from "@globalTypes"
import { useWindowSize } from "@lib/hooks/use-window-size"
import React from "react"
import { useMedia } from "use-media"
import ArticleInfo from "./ArticleInfo"
import CombinedImage from "./CombinedImage"
import { DesktopCarousel } from "./DesktopCarousel"
import EmptyState from "./EmptyState"
import LoadingState from "./LoadingState"
import { MobileCarousel } from "./MobileCarousel"
import Navigation from "./Navigation"
import Tabs from "./Tabs"
import * as S from "./elements"
import { useConfiguratorData } from "./hooks/use-configurator-data"
import { useConfiguratorNavigation } from "./hooks/use-configurator-navigation"
import { useConfiguratorTabs } from "./hooks/use-configurator-tabs"

export type ConfiguratorProductType = "ring" | "earring"
export type ActionType =
  | { type: "set-component"; component: ComponentFilter }
  | { type: "" }

export const ConfiguratorV2: React.FC = () => {
  const { tab, setTab, allowedTabs } = useConfiguratorTabs()
  const { configuratorStatus, queryStatus, items } = useConfiguratorData(tab)
  const {
    status: navigationStatus,
    activeIndex,
    activeItem,
    prev,
    next,
    goTo,
    canMovePrev,
    canMoveNext,
  } = useConfiguratorNavigation(items, tab)
  const isSmall = useMedia(`screen and (max-width: ${breakpoints.medium})`)
  const { isResizing } = useWindowSize()
  if (configuratorStatus === "invalid") {
    return <EmptyState />
  }
  // The configurator is still loading, display a loading state
  if (
    configuratorStatus === "loading" ||
    queryStatus === "loading" ||
    navigationStatus === "loading"
  ) {
    return <LoadingState />
  }

  // Ready to go!
  return (
    <>
      <>
        <SEO title="Configurator">
          <meta name="robots" content="noindex,nofollow" />
        </SEO>
        <S.BackgroundContainer />
        <S.Grid as="section">
          <S.Row order={[4, 0, 0]} pt={[2, 0]}>
            <Navigation />
          </S.Row>
          <Box
            order={[0, 1, 1]}
            display="flex"
            alignItems="center"
            position="relative"
          >
            <CombinedImage />
            {!isSmall && !isResizing && (
              // Hide the carousel during resizing to enforce
              // re-calculation on the virtualized list
              <DesktopCarousel
                tab={tab}
                activeIndex={activeIndex}
                items={items}
                onClickPrev={prev}
                canMovePrev={canMovePrev}
                onClickNext={next}
                canMoveNext={canMoveNext}
                onClickItem={goTo}
              />
            )}
          </Box>

          {!isSmall && (
            <S.Row
              order={[4, 2]}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ArticleInfo tab={tab} activeItem={activeItem} />
            </S.Row>
          )}
          <S.Row order={[2, 4]} py={[".5rem", 0]}>
            <Tabs active={tab} onSetTab={setTab} allowedTabs={allowedTabs} />
          </S.Row>

          {isSmall && (
            <S.Row order={1} px="0" display="flex">
              {!isResizing && (
                // Hide the carousel during resizing to enforce
                // re-calculation on the virtualized list
                <MobileCarousel
                  activeIndex={activeIndex}
                  items={items}
                  onClickPrev={prev}
                  canMovePrev={canMovePrev}
                  onClickNext={next}
                  canMoveNext={canMoveNext}
                  onClickItem={goTo}
                />
              )}
            </S.Row>
          )}
        </S.Grid>
      </>
    </>
  )
}
